import { useRef, useState } from "react";
import { Upload, Receipt } from "lucide-react";

const ALLOWED = ["application/pdf", "image/jpeg", "image/png"];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const TONE = { pending_verification: "bg-amber-50 border-amber-200", verified: "bg-emerald-50 border-emerald-200", rejected: "bg-red-50 border-red-200" };

export function PortalPaymentProof({ payment, onUpload }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  if (!payment || (!payment.can_upload && !payment.proofs?.length)) return null;

  async function pick(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED.includes(f.type) && !["pdf", "jpg", "jpeg", "png"].includes(ext)) { setMsg({ err: true, text: "Formato no permitido. Usa PDF, JPG o PNG." }); return; }
    if (f.size > payment.max_mb * 1024 * 1024) { setMsg({ err: true, text: `El archivo excede ${payment.max_mb} MB.` }); return; }
    setBusy(true); setMsg(null);
    try { await onUpload(f); setMsg({ err: false, text: "Recibimos tu comprobante. El taller lo verificará." }); }
    catch (err) { setMsg({ err: true, text: err?.response?.data?.detail || "No se pudo subir el archivo. Intenta de nuevo." }); }
    finally { setBusy(false); }
  }

  return (
    <div className="bg-white border border-border rounded-xl p-5" data-testid="portal-payment">
      <div className="flex items-center gap-2 mb-3">
        <Receipt className="h-4 w-4" style={{ color: "var(--tenant-primary)" }} />
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Comprobante de pago</div>
      </div>
      <div className="space-y-2 mb-3">
        {payment.proofs.map((p) => (
          <div key={p.id} className={`border rounded-lg px-3 py-2 text-sm ${TONE[p.status] || ""}`} data-testid={`portal-proof-${p.id}`}>
            <div className="font-medium" data-testid={`portal-proof-status-${p.id}`}>{p.status_label}</div>
            <div className="text-xs text-muted-foreground"><span className="[overflow-wrap:anywhere]">{p.filename}</span> · {fmtDate(p.uploaded_at)} · Cotización {p.quote_number}</div>
            {p.status === "rejected" && p.rejection_reason && <div className="text-xs text-red-800 mt-1">Motivo: {p.rejection_reason}</div>}
          </div>
        ))}
      </div>
      {payment.can_upload && (
        <>
          <p className="text-sm text-slate-700 mb-3">
            {payment.is_replacement ? "Puedes reemplazar tu comprobante mientras el taller lo verifica." : `Si ya pagaste la cotización ${payment.quote_number}, sube tu comprobante.`}
            <span className="block text-xs text-muted-foreground mt-1">PDF, JPG o PNG · máximo {payment.max_mb} MB. No compartas datos de tu tarjeta.</span>
          </p>
          <input ref={input} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" onChange={pick} data-testid="portal-proof-input" />
          <button type="button" disabled={busy} onClick={() => input.current?.click()} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-lg text-white text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-60" style={{ background: "var(--tenant-primary)" }} data-testid="portal-proof-upload">
            <Upload className="h-4 w-4" /> {busy ? "Subiendo…" : payment.is_replacement ? "Reemplazar comprobante" : "Subir comprobante"}
          </button>
        </>
      )}
      {msg && <div className={`text-sm mt-2 ${msg.err ? "text-red-600" : "text-emerald-700"}`} data-testid="portal-proof-message">{msg.text}</div>}
    </div>
  );
}
